import Swiper from "./swiper-bundle.esm.browser.min.js";

const loadStylesheet = (href) => {
  if (!href) return Promise.resolve();

  const existing = Array.from(document.querySelectorAll('link[rel="stylesheet"], link[rel="preload"][as="style"]')).find(
    (link) => link.href === href || link.getAttribute("href") === href,
  );
  if (existing) {
    if (existing.rel === "stylesheet") return Promise.resolve();
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", resolve, { once: true });
      existing.addEventListener("error", reject, { once: true });
    });
  }

  return new Promise((resolve, reject) => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    link.addEventListener("load", resolve, { once: true });
    link.addEventListener("error", reject, { once: true });
    document.head.appendChild(link);
  });
};

const photoswipeRoot = document.querySelector(".photoswipe");
if (photoswipeRoot) {
  const photoswipeCss = photoswipeRoot.getAttribute("data-photoswipe-css");
  loadStylesheet(photoswipeCss)
    .catch(() => undefined)
    .then(() => import("./photoswipe-lightbox.esm.min.js"))
    .then(({ default: PhotoSwipeLightbox }) => {
      const badges = document.querySelector(".photoswipe > .card__badges");

      const lightbox = new PhotoSwipeLightbox({
        // may select multiple "galleries"
        gallery: ".photoswipe",

        // Elements within gallery (slides)
        children: "a",

        // setup PhotoSwipe Core dynamic import
        pswpModule: () => import("./photoswipe.esm.min.js"),

        bgOpacity: 1,
      });
      lightbox.init();
      lightbox.on("beforeOpen", () => {
        badges?.classList.add("hide");
      });

      lightbox.on("destroy", () => {
        badges?.classList.remove("hide");
      });
    })
    .catch((error) => console.error("PhotoSwipe failed to load:", error));
}

if (!customElements.get("gallery-section")) {
  customElements.define(
    "gallery-section",
    class GallerySection extends HTMLElement {
      updateSwiperAutoHeight = () => {
        if (this.resolution != this.matchResolution()) {
          this.resolution = this.matchResolution();
          this.destroyCarouselGallery();
          this.destroyCollageCarouselGallery();
          this._galleryLayoutMode = null;
          this.initializeGallery();
          this.setStickyGallery();
        }
      };

      constructor() {
        super();
      }

      connectedCallback() {
        this.isProductPage = this.dataset.productPage === "";

        this.galleryLoader = this.querySelector("#gallery-loader");
        this.renderedSlides = [];
        this.readConfiguration();
        this.initializeGallery();
        this.revealGallery();

        this.setStickyGallery();
      }

      revealGallery() {
        const reveal = () => {
          this.galleryLoader?.classList.add("hidden");
          this.classList.remove("loading");
          this.classList.add("loaded");
          // Collage two-up may have measured while opacity:0; refresh widths.
          this.gallerySwiper?.update();
          // Videos may have been skipped while .loading was on — kick them now.
          window.refreshPdpGalleryVideos?.();
        };

        if (!this.galleryLoader) {
          reveal();
          return;
        }

        const whenImageReady = (image) => {
          if (!image || image.complete) return Promise.resolve();
          return new Promise((resolve) => {
            image.addEventListener("load", resolve, { once: true });
            image.addEventListener("error", resolve, { once: true });
          });
        };

        // Uncover as soon as the first real image is ready. Remaining tiles keep
        // their own per-slide skeletons via pdp-media-lazy.js.
        const firstImage = this.querySelector("[data-gallery] .wt-product__img");
        const safetyTimeout = new Promise((resolve) => {
          window.setTimeout(resolve, 2000);
        });

        Promise.race([whenImageReady(firstImage), safetyTimeout]).then(() => {
          window.requestAnimationFrame(reveal);
        });
      }

      disconnectedCallback() {
        // this.destroyGallery();
      }

      setStickyGallery() {
        if (this.isProductPage) {
          const headerHeight =
            document.querySelector(".wt-header")?.offsetHeight;
          const swiperRect = this.getBoundingClientRect();

          const fitsInViewport =
            swiperRect.top >= 0 &&
            swiperRect.left >= 0 &&
            swiperRect.height <= window.innerHeight;

          const positionTopValue = `${headerHeight + 16}px`;

          this.style.setProperty("--position-top", positionTopValue);
          this.classList.add("wt-product__gallery--sticky", fitsInViewport);
        }
      }

      readConfiguration() {
        this.lastMediaSelection = null;
        // general configuration
        this.configuration = [];

        this.elements = {
          section: this,
          gallery: this.querySelector("[data-gallery]"),
          thumbs: this.querySelector("[data-thumbs]"),
          galleryContainer: this.querySelector("[data-gallery]").querySelector(
            "[data-swiper-container]",
          ),
          thumbsContainer: this.querySelector("[data-thumbs]").querySelector(
            "[data-swiper-container]",
          ),
        };

        this.elements.gallerySlides = Array.from(
          this.elements.gallery.querySelectorAll("[data-swiper-slide]"),
        ).map((e) => e.cloneNode(true));
        this.elements.thumbsSlides = Array.from(
          this.elements.thumbs.querySelectorAll("[data-swiper-slide]"),
        ).map((e) => e.cloneNode(true));

        const default_configuration = {
          sliderEnabledBreakpoint: 900,
          desktopLayout: "carousel-vertical",
        };

        const custom_configuration = JSON.parse(
          this.elements.section.querySelector("[data-configuration]").innerHTML,
        );
        this.configuration = {
          ...default_configuration,
          ...custom_configuration,
        };

        this.mediaOrderMap = new Map(
          (this.configuration.mediaOrder || []).map((id, index) => [
            Number(id),
            index,
          ]),
        );

        let autoHeightEnabled = window.innerWidth <= 768;

        const UNMUTE_CLASS_TOGGLE = "wt-hero-video__sound-toggle--unmuted";

        function handleSlideVideos(swiper) {
          const allVideos = Array.from(swiper.slides).flatMap((slide) =>
            Array.from(slide.querySelectorAll("video")),
          );
          const allToggleBtns = Array.from(swiper.slides).flatMap((slide) =>
            Array.from(slide.querySelectorAll(".wt-hero-video__sound-toggle")),
          );

          // pause, mute & disable controls for all videos
          allVideos.forEach((video) => {
            video.pause();
            video.currentTime = 0;
            video.muted = true;
            video.controls = false;
            clearTimeout(video._hideCtrlsTimer);
          });

          // reset all toggle buttons to "off" state
          allToggleBtns.forEach((btn) => {
            btn.setAttribute("data-sound", "off");
            btn.classList.remove(UNMUTE_CLASS_TOGGLE);
          });

          // Play videos in the visible window (1 for single, 2 for collage carousel).
          const perView = Math.max(1, Math.round(Number(swiper.params?.slidesPerView) || 1));
          const start = swiper.activeIndex || 0;
          for (let i = start; i < start + perView; i++) {
            const slide = swiper.slides?.[i];
            const activeVideo = slide?.querySelector?.("video");
            if (!activeVideo) continue;
            window.activatePdpVideo?.(activeVideo);
            activeVideo._pdpVideoUI?.showLoading?.();
            activeVideo.muted = true;
            activeVideo.play().catch((err) => {
              console.warn("Video playback failed:", err);
            });
          }
        }

        function initVideoInteractions(swiper) {
          handleSlideVideos(swiper);

          // add delegation listener for clicks
          swiper.el.addEventListener("click", (e) => {
            // Video element tapped -> show native controls temporarily
            const video = e.target.closest("video");
            if (video) {
              video.controls = true;
              video.blur();
              clearTimeout(video._hideCtrlsTimer);
              video._hideCtrlsTimer = setTimeout(() => {
                video.controls = false;
              }, 3000);
              return;
            }
          });
        }

        // gallery swiper configuration
        const default_gallery_configuration = {
          autoHeight: autoHeightEnabled,
          threshold: 10,
          grabCursor: true,
          navigation: {
            nextEl: ".wt-slider__nav-next",
            prevEl: ".wt-slider__nav-prev",
          },
          scrollbar: {
            el: ".wt-slider__scrollbar",
            draggable: true,
          },
          pagination: {
            el: ".swiper-pagination",
            type: "fraction",
          },
          on: {
            afterInit: initVideoInteractions,
            slideChangeTransitionEnd: handleSlideVideos,
          },
        };

        const custom_gallery_configuration = JSON.parse(
          this.elements.gallery.querySelector("[data-swiper-configuration]")
            .innerHTML,
        );
        this.gallery_configuration = {
          ...default_gallery_configuration,
          ...custom_gallery_configuration,
        };

        const updateThumbsSwiperClasses = (swiperInstance) => {
          const container = swiperInstance.el;
          const cls = {
            beginning: "swiper-at-beginning",
            end: "swiper-at-end",
            locked: "swiper-locked",
            ready: "swiper-thumbs-ready",
          };

          container.classList.remove(
            cls.beginning,
            cls.end,
            cls.locked,
            cls.ready,
          );

          if (swiperInstance.isLocked) {
            container.classList.add(cls.locked);
            return;
          }

          container.classList.add(cls.ready);

          if (swiperInstance.isBeginning)
            container.classList.add(cls.beginning);
          if (swiperInstance.isEnd) container.classList.add(cls.end);
        };

        // gallery thumbs swiper configuration
        const default_thumbs_configuration = {
          grabCursor: true,
          spaceBetween: 8,
          slidesPerView: 4,
          freeMode: false,
          threshold: 5,
          direction: "horizontal",
          watchSlidesVisibility: true,
          watchSlidesProgress: true,
          watchOverflow: true,
          navigation: {
            nextEl: ".wt-slider__nav-next",
            prevEl: ".wt-slider__nav-prev",
          },
          on: {
            afterInit(swiper) {
              updateThumbsSwiperClasses(swiper);
            },
            lock: updateThumbsSwiperClasses,
            unlock: updateThumbsSwiperClasses,
            slideChange: updateThumbsSwiperClasses,
            observerUpdate: updateThumbsSwiperClasses,
            resize: updateThumbsSwiperClasses,
          },
        };

        const custom_thumbs_configuration = JSON.parse(
          this.elements.thumbs.querySelector("[data-swiper-configuration]")
            .innerHTML,
        );
        this.thumbs_configuration = {
          ...default_thumbs_configuration,
          ...custom_thumbs_configuration,
        };
        this.resolution = this.matchResolution();
      }

      initializeGallery() {
        if (!this._onGalleryResize) {
          this._onGalleryResize = () => {
            this.updateSwiperAutoHeight(this.gallerySwiper);
            if (this.configuration.desktopLayout === "masonry") {
              this.handleMassonry();
            } else if (this.configuration.desktopLayout === "collage") {
              this.handleCollage();
            }
          };
          window.addEventListener("resize", this._onGalleryResize);
        }

        switch (this.configuration.desktopLayout) {
          case "carousel-vertical":
          case "carousel-horizontal":
            this.initializeCarouselGallery();
            break;
          case "masonry":
            this.handleMassonry();
            break;
          case "collage":
            this.handleCollage();
            break;
        }
      }

      initializeCarouselGallery() {
        if (this.thumbsSwiper == null) {
          this.decorateSwiper(
            this.elements.thumbs,
            "wt-slider__container--thumbs",
          );
          this.thumbsSwiper = this.swiperThumbsInitilize();
        }
        this.decorateSwiper(this.elements.gallery);
        this.swiperGalleryInitilize(this.thumbsSwiper);

        const disableTouchSlide =
          this.elements.gallery.querySelector(".disable-touch");
        if (disableTouchSlide) {
          disableTouchSlide.classList.add("swiper-no-swiping");
        }
      }

      swiperGalleryInitilize(thumbsSwiper) {
        if (this.gallerySwiper == null) {
          const thumbs_configuration = {
            thumbs: {
              swiper: thumbsSwiper,
            },
          };
          const desktopRatio = this.getAttribute("desktop-ratio");

          const useAlwaysAutoHeight =
            (this.configuration.desktopLayout === "carousel-vertical" &&
              desktopRatio === "original") ||
            this.configuration.desktopLayout === "carousel-horizontal";

          let autoHeightEnabled = useAlwaysAutoHeight
            ? true
            : window.innerWidth <= 900;
          if (thumbsSwiper)
            this.gallery_configuration = {
              ...this.gallery_configuration,
              ...thumbs_configuration,
              autoHeight: autoHeightEnabled,
            };
          this.gallerySwiper = new Swiper(
            this.elements.gallery,
            this.gallery_configuration,
          );
        }
      }

      swiperThumbsInitilize() {
        let autoHeightEnabled = window.innerWidth <= 900;
        this.thumbs_configuration = {
          ...this.thumbs_configuration,
          autoHeight: autoHeightEnabled,
        };
        const swiper = new Swiper(
          this.elements.thumbs,
          this.thumbs_configuration,
        );
        return swiper;
      }

      destroyCarouselGallery() {
        if (this.thumbsSwiper != null) {
          this.thumbsSwiper.destroy();
          this.thumbsSwiper = null;
          this.undecorateSwiper(
            this.elements.thumbs,
            "wt-slider__container--thumbs",
          );
        }
        if (this.gallerySwiper != null) {
          this.gallerySwiper.destroy();
          this.undecorateSwiper(this.elements.gallery);
          this.gallerySwiper = null;
        }
      }

      handleMassonry() {
        const mode =
          this.matchResolution() === "desktop"
            ? "desktop-masonry"
            : "mobile-carousel";

        if (this._galleryLayoutMode === mode) {
          this.gallerySwiper?.update();
          return;
        }

        const previousIndex = this.gallerySwiper?.activeIndex ?? 0;

        if (mode === "desktop-masonry") {
          this.destroyCarouselGallery();
          this.initializeMasonryGallery();
        } else {
          this.destroyMasonryGallery();
          this.initializeCarouselGallery();
        }

        this._galleryLayoutMode = mode;
        this.restoreGalleryIndex(previousIndex);
      }

      handleCollage() {
        const mode =
          this.matchResolution() === "desktop"
            ? "desktop-collage"
            : "mobile-carousel";

        // Mobile address-bar show/hide fires resize without a real layout
        // change. Rebuilding Swiper here would jump back to slide 0.
        if (this._galleryLayoutMode === mode) {
          this.gallerySwiper?.update();
          return;
        }

        const previousIndex = this.gallerySwiper?.activeIndex ?? 0;

        if (mode === "desktop-collage") {
          this.destroyCarouselGallery();
          this.destroyCollageGallery();
          this.initializeCollageCarouselGallery();
        } else {
          this.destroyCollageCarouselGallery();
          this.destroyCollageGallery();
          this.initializeCarouselGallery();
        }

        this._galleryLayoutMode = mode;
        this.restoreGalleryIndex(previousIndex);
      }

      restoreGalleryIndex(index) {
        if (!this.gallerySwiper || !index || index < 1) return;
        const max = Math.max(0, (this.gallerySwiper.slides?.length || 1) - 1);
        const target = Math.min(index, max);
        this.gallerySwiper.slideTo(target, 0);
        this.thumbsSwiper?.slideTo(target, 0);
      }

      decorateSwiper(el, element_class) {
        el.classList.add("swiper", "wt-slider__container", element_class);
        el.querySelector("[data-swiper-container]")?.classList.add(
          "swiper-wrapper",
          "wt-slider__wrapper",
        );
        el.querySelectorAll("[data-swiper-slide]").forEach(function (e) {
          e.classList.add("swiper-slide", "wt-slider__slide");
        });
        this.galleryUpdateEvent();
      }

      undecorateSwiper(el, element_class) {
        el.classList.remove("swiper", "wt-slider__container", element_class);
        el.querySelector("[data-swiper-container]").classList.remove(
          "swiper-wrapper",
          "wt-slider__wrapper",
        );
        el.querySelectorAll("[data-swiper-slide]").forEach(function (e) {
          e.classList.remove("swiper-slide", "wt-slider__slide");
        });
      }

      decorateCollage(el, element_class) {
        el.classList.add(`wt-${element_class}`);
        el.querySelector("[data-swiper-container]").classList.add(
          `wt-${element_class}__wrapper`,
        );
        el.querySelectorAll("[data-swiper-slide]").forEach(function (e) {
          e.classList.add(`wt-${element_class}__slide`);
        });
        this.galleryUpdateEvent();
      }

      undecorateCollage(el, element_class) {
        el.classList.remove(`wt-${element_class}`);
        el.querySelector("[data-swiper-container]").classList.remove(
          `wt-${element_class}__wrapper`,
        );
        el.querySelectorAll("[data-swiper-slide]").forEach(function (e) {
          e.classList.remove(`wt-${element_class}__slide`);
        });
      }

      initializeCollageGallery() {
        this.decorateCollage(this.elements.gallery, "collage");
      }

      destroyCollageGallery() {
        this.undecorateCollage(this.elements.gallery, "collage");
      }

      /**
       * Desktop collage: two slides visible, left/right navigation.
       * Mobile still uses the regular single-slide carousel via handleCollage().
       */
      preloadCollageCarouselAhead(swiper) {
        if (!swiper) return;
        // Two visible tiles + one ahead so the next right-click is already warm.
        const from = swiper.activeIndex;
        window.preloadPdpGallerySlides?.(this.elements.gallery, from, 3);

        for (let i = from; i < from + 3; i++) {
          const slide = swiper.slides?.[i];
          const video = slide?.querySelector?.("video");
          if (!video) continue;
          window.activatePdpVideo?.(video);
          video._pdpVideoUI?.showLoading?.();
        }
      }

      initializeCollageCarouselGallery() {
        if (this.gallerySwiper != null) return;

        this.classList.add("wt-product__gallery--collage-carousel");
        this.decorateSwiper(this.elements.gallery);

        const galleryEl = this.elements.gallery;
        const nextEl = galleryEl.querySelector(":scope > .wt-slider__nav-next");
        const prevEl = galleryEl.querySelector(":scope > .wt-slider__nav-prev");
        const baseOn = this.gallery_configuration.on || {};
        const self = this;

        const config = {
          ...this.gallery_configuration,
          slidesPerView: 2,
          slidesPerGroup: 1,
          spaceBetween: 8,
          watchOverflow: true,
          autoHeight: false,
          grabCursor: true,
          navigation: {
            nextEl: nextEl || galleryEl.querySelector(".wt-slider__nav-next"),
            prevEl: prevEl || galleryEl.querySelector(".wt-slider__nav-prev"),
          },
          scrollbar: false,
          pagination: false,
          on: {
            ...baseOn,
            afterInit(swiper) {
              baseOn.afterInit?.(swiper);
              self.preloadCollageCarouselAhead(swiper);
            },
            slideChange(swiper) {
              baseOn.slideChange?.(swiper);
              self.preloadCollageCarouselAhead(swiper);
            },
          },
        };
        delete config.thumbs;

        this.gallerySwiper = new Swiper(galleryEl, config);

        // Warm the next-after-visible tile as soon as the user aims right.
        const warmNext = () => {
          const swiper = this.gallerySwiper;
          if (!swiper) return;
          window.preloadPdpGallerySlides?.(
            this.elements.gallery,
            swiper.activeIndex + 2,
            1,
          );
        };
        nextEl?.addEventListener("pointerdown", warmNext, { passive: true });
        nextEl?.addEventListener("mouseenter", warmNext, { passive: true });
      }

      destroyCollageCarouselGallery() {
        this.classList.remove("wt-product__gallery--collage-carousel");
        if (this.gallerySwiper != null) {
          this.gallerySwiper.destroy();
          this.undecorateSwiper(this.elements.gallery);
          this.gallerySwiper = null;
        }
      }

      initializeMasonryGallery() {
        this.decorateCollage(this.elements.gallery, "masonry");
      }

      destroyMasonryGallery() {
        this.undecorateCollage(this.elements.gallery, "masonry");
      }

      getSlideMediaId(slide) {
        let media = slide.querySelector("img");
        if (!media) media = slide.querySelector("video");

        const mediaId =
          media?.getAttribute("data-media-id") ||
          slide.dataset?.mediaId ||
          slide.dataset?.slideMediaId;

        return mediaId ? Number(mediaId) : null;
      }

      getSlideCanonicalIndex(slide, domIndex) {
        const mediaId = this.getSlideMediaId(slide);

        if (mediaId != null && this.mediaOrderMap?.has(mediaId)) {
          return this.mediaOrderMap.get(mediaId);
        }

        return domIndex;
      }

      getSlideSortTier(slide, colorValues, featuredId, allColorOptions) {
        const mediaId = this.getSlideMediaId(slide);

        if (featuredId && mediaId === featuredId) {
          return 0;
        }

        if (this.slideHasColorMatch(slide, colorValues, allColorOptions)) {
          return 1;
        }

        return 2;
      }

      compareSlidesByOriginalOrder(a, b) {
        if (a.tier !== b.tier) {
          return a.tier - b.tier;
        }

        if (a.canonicalIndex !== b.canonicalIndex) {
          return a.canonicalIndex - b.canonicalIndex;
        }

        return a.domIndex - b.domIndex;
      }

      sortSlides(slides, featured_media_id) {
        const featuredIndex = slides.findIndex(
          (slide) => this.getSlideMediaId(slide) === Number(featured_media_id),
        );

        if (featuredIndex > -1) {
          const featuredSlide = slides[featuredIndex];
          slides.splice(featuredIndex, 1);
          slides.unshift(featuredSlide);
        }

        return slides;
      }

      beforeGalleryChange(showFullSkeleton = true) {
        if (showFullSkeleton) {
          this.classList.add("loading");
          this.galleryLoader?.classList.remove("hidden");
        }
        this.style.minHeight = `${this.offsetHeight}px`;
      }

      afterGalleryChange() {
        this.style.minHeight = "unset";
        this.galleryLoader?.classList.add("hidden");
        this.classList.remove("loading");
        this.querySelectorAll("[data-gallery] .wt-product__img").forEach((image) => {
          window.bindPdpMediaSkeleton?.(image);
        });
        this.querySelectorAll("[data-gallery] video").forEach((video) => {
          window.bindPdpMediaVideo?.(video);
        });
        this.ensureGalleryVisible();
      }

      /**
       * After color-based reorder, matching images move to the top of the
       * collage. If the user had scrolled down, bring the gallery back into view.
       */
      ensureGalleryVisible() {
        const headerHeight =
          document.querySelector(".wt-header")?.offsetHeight || 0;
        const topOffset = headerHeight + 16;
        const galleryRect = this.getBoundingClientRect();

        if (galleryRect.top >= topOffset) return;

        const targetY = window.scrollY + galleryRect.top - topOffset;
        window.scrollTo({ top: Math.max(0, targetY), behavior: "smooth" });
      }

      getSlideAlt(slide) {
        let media = slide.querySelector("img");
        if (media == null) media = slide.querySelector("video");
        return media ? media.getAttribute("alt") : "";
      }

      isColorOptionFieldset(fieldset) {
        const bodyClass =
          fieldset.querySelector('[class*="wt-product__option__body--"]')
            ?.className || "";

        if (/wt-product__option__body--(?:swatches|thumbs)/.test(bodyClass)) {
          return true;
        }

        const label = fieldset
          .querySelector(".wt-product__option__title .label")
          ?.textContent?.replace(":", "")
          .trim()
          .toLowerCase();

        return ["color", "colour", "couleur", "farbe", "colori"].includes(
          label,
        );
      }

      getVariantOptionsElement() {
        const sectionId = this.id?.replace(/^MediaGallery-/, "");
        return sectionId
          ? document.querySelector(`variant-options[data-section="${sectionId}"]`)
          : null;
      }

      getColorOptionFieldsets(variantOptions = this.getVariantOptionsElement()) {
        if (!variantOptions) return [];

        return Array.from(
          variantOptions.querySelectorAll(".wt-product__option"),
        ).filter((fieldset) => this.isColorOptionFieldset(fieldset));
      }

      getAllColorOptionValues() {
        return this.getColorOptionFieldsets()
          .flatMap((fieldset) =>
            Array.from(fieldset.querySelectorAll("input")).map(
              (radio) => radio.value,
            ),
          )
          .filter(Boolean);
      }

      getSelectedColorValues(options = []) {
        const colors = this.getColorOptionFieldsets()
          .map(
            (fieldset) =>
              Array.from(fieldset.querySelectorAll("input")).find(
                (radio) => radio.checked,
              )?.value,
          )
          .filter(Boolean);

        if (colors.length) return colors;

        return options.filter(Boolean);
      }

      escapeRegExp(string) {
        return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      }

      altMatchesColorPhrase(alt, colorValue) {
        if (!alt || !colorValue) return false;

        const pattern = new RegExp(
          `\\b${this.escapeRegExp(colorValue.trim())}\\b`,
          "i",
        );

        return pattern.test(alt);
      }

      getMatchingColorFromAlt(alt, colorOptions) {
        if (!alt || !colorOptions?.length) return null;

        const sortedColors = [...new Set(colorOptions)]
          .filter(Boolean)
          .sort((a, b) => b.length - a.length || a.localeCompare(b));

        return (
          sortedColors.find((color) => this.altMatchesColorPhrase(alt, color)) ||
          null
        );
      }

      slideHasColorMatch(slide, colorValues, allColorOptions) {
        if (!colorValues.length) return false;

        const alt = this.getSlideAlt(slide);
        if (!alt) return false;

        const colorOptions = allColorOptions?.length
          ? allColorOptions
          : colorValues;
        const matchedColor = this.getMatchingColorFromAlt(alt, colorOptions);
        if (!matchedColor) return false;

        const normalizedMatched = matchedColor.toLowerCase().trim();
        return colorValues.some(
          (color) => color.toLowerCase().trim() === normalizedMatched,
        );
      }

      sortSlidesByColorOption(slides, options, featured_media_id) {
        const colorValues = this.getSelectedColorValues(options);
        const allColorOptions = this.getAllColorOptionValues();
        const hasColorMatches = slides.some((slide) =>
          this.slideHasColorMatch(slide, colorValues, allColorOptions),
        );

        if (!hasColorMatches) {
          if (featured_media_id) {
            return this.sortSlides([...slides], featured_media_id);
          }
          return slides;
        }

        const featuredId = featured_media_id ? Number(featured_media_id) : null;

        return [...slides]
          .map((slide, domIndex) => ({
            slide,
            domIndex,
            tier: this.getSlideSortTier(
              slide,
              colorValues,
              featuredId,
              allColorOptions,
            ),
            canonicalIndex: this.getSlideCanonicalIndex(slide, domIndex),
          }))
          .sort((a, b) => this.compareSlidesByOriginalOrder(a, b))
          .map(({ slide }) => slide);
      }

      galleryUpdateEvent(opt) {
        const galleryEvent = new CustomEvent("gallery:updated", {
          bubbles: true,
          cancelable: true,
          detail: {
            desc: "gallery updated",
            selector: ".wt-product__gallery",
            ...opt,
          },
        });
        document.dispatchEvent(galleryEvent);
      }

      filterSlides(options, featured_media_id, matchAll = true, callback) {
        if (!this.elements) return;
        const mediaSelection = JSON.stringify([
          this.getSelectedColorValues(options),
          featured_media_id || null,
        ]);
        // Size changes usually select the same media. Do not clone the entire
        // gallery, measure Swiper, or reset a customer's slide in that case.
        if (this.lastMediaSelection === mediaSelection) {
          if (typeof callback === "function") callback();
          return;
        }
        const originalGallerySlides = this.elements.gallerySlides;
        const originalThumbsSlides = this.elements.thumbsSlides;

        const getMediaId = (item) =>
          item?.dataset?.mediaId || item?.dataset?.slideMediaId;

        let filteredGallerySlides = this.sortSlidesByColorOption(
          originalGallerySlides,
          options,
          featured_media_id,
        );
        let filteredThumbsSlides = this.sortSlidesByColorOption(
          originalThumbsSlides,
          options,
          featured_media_id,
        );

        // Skip DOM rebuild only when the target order already matches what is
        // on screen (or what we last rendered). Incomplete IDs force a rebuild
        // so we never no-op on a partial/incorrect comparison.
        const currentDomIds = Array.from(
          this.elements.gallery?.querySelectorAll("[data-swiper-slide]") || [],
        ).map(getMediaId);
        const filteredIds = filteredGallerySlides.map(getMediaId);
        const previousIds = this.renderedSlides.length
          ? this.renderedSlides.map(getMediaId)
          : currentDomIds;
        const canSkipRebuild =
          filteredIds.length > 0 &&
          filteredIds.length === previousIds.length &&
          filteredIds.length === originalGallerySlides.length &&
          filteredIds.every(Boolean) &&
          previousIds.every(Boolean) &&
          previousIds.toString() === filteredIds.toString();
        const renderedSlidesChanged = !canSkipRebuild;

        // Compare lightweight references first; clone only for a real reorder.
        if (renderedSlidesChanged) {
          filteredGallerySlides = filteredGallerySlides.map((slide) => slide.cloneNode(true));
          filteredThumbsSlides = filteredThumbsSlides.map((slide) => slide.cloneNode(true));
        }

        if (this.gallerySwiper && this.thumbsSwiper && renderedSlidesChanged) {
          this.beforeGalleryChange(false);
          const thumbSlidesWrapper = this.querySelector(
            "[data-thumbs] [data-swiper-container]",
          );
          const gallerySlidesWrapper = this.querySelector(
            "[data-gallery] [data-swiper-container]",
          );

          // Assuming this.gallerySwiper is the swiper instance for the main gallery
          this.gallerySwiper.removeAllSlides();
          gallerySlidesWrapper.innerHTML = "";
          const swiperContainer = this.querySelector(".swiper-wrapper");
          filteredGallerySlides.forEach((slide, idx) => {
            if (idx === 0) {
              const slideImg = slide.querySelector("img");
              if (slideImg)
                slideImg.onload = function () {
                  swiperContainer.style.height = "auto";
                };
            }
          });
          // Swiper recalculates layout on appendSlide. Append the complete set
          // once instead of forcing a layout for every image and thumbnail.
          this.gallerySwiper.appendSlide(filteredGallerySlides);
          this.decorateSwiper(this.elements.gallery);

          // Assuming this.thumbsSwiper is the swiper instance for the thumbnails
          this.thumbsSwiper.removeAllSlides();
          thumbSlidesWrapper.innerHTML = "";

          this.thumbsSwiper.appendSlide(filteredThumbsSlides);

          this.decorateSwiper(
            this.elements.thumbs,
            "wt-slider__container--thumbs",
          );

          this.thumbsSwiper.update();
          this.gallerySwiper.update();
        } else if (
          this.configuration.desktopLayout == "collage" ||
          this.configuration.desktopLayout == "masonry"
        ) {
          if (renderedSlidesChanged) {
            this.beforeGalleryChange(false);

            const isDesktopCollageCarousel =
              this.configuration.desktopLayout == "collage" &&
              this.matchResolution() == "desktop" &&
              this.gallerySwiper != null;

            this.elements.thumbs
              .querySelectorAll("[data-swiper-slide]")
              .forEach((slide) => slide.remove());

            if (isDesktopCollageCarousel) {
              this.gallerySwiper.removeAllSlides();
              this.gallerySwiper.appendSlide(filteredGallerySlides);
              filteredThumbsSlides.forEach((slide) =>
                this.elements.thumbs
                  .querySelector("[data-swiper-container]")
                  .append(slide),
              );
              this.decorateSwiper(this.elements.gallery);
              this.gallerySwiper.update();
            } else {
              this.elements.gallery
                .querySelectorAll("[data-swiper-slide]")
                .forEach((slide) => slide.remove());

              filteredGallerySlides.forEach((slide) =>
                this.elements.gallery
                  .querySelector("[data-swiper-container]")
                  .append(slide),
              );
              filteredThumbsSlides.forEach((slide) =>
                this.elements.thumbs
                  .querySelector("[data-swiper-container]")
                  .append(slide),
              );

              this.decorateCollage(
                this.elements.gallery,
                this.configuration.desktopLayout,
              );
            }

            // add wt-product__gallery --even --odd classes to MediaGallery container depends on number of slides
            this.elements.gallery.classList.remove("wt-product__gallery--even");
            this.elements.gallery.classList.remove("wt-product__gallery--odd");
            if (filteredGallerySlides.length % 2 == 0) {
              this.elements.gallery.classList.add("wt-product__gallery--even");
            } else {
              this.elements.gallery.classList.add("wt-product__gallery--odd");
            }
          }
        }

        if (featured_media_id) {
          this.setActiveMedia(featured_media_id, true);
        } else {
          this.setActiveMedia(
            filteredGallerySlides[0]
              ?.querySelector("img")
              ?.getAttribute("data-media-id"),
            true,
          );
        }
        if (renderedSlidesChanged) {
          // A color switch only reorders media. Keep the gallery visible while
          // doing that work: cached images remain painted, and genuinely new
          // images get their own per-slide skeleton from pdp-media-lazy.js.
          // The full-gallery skeleton is reserved for the initial PDP load.
          this.afterGalleryChange();
        }

        this.renderedSlides = [...filteredGallerySlides];
        this.lastMediaSelection = mediaSelection;
      }

      setActiveMedia(mediaId, prepend) {
        let media = this.elements.gallery.querySelector(
          `[data-media-id="${mediaId}"]`,
        );

        if (this.gallerySwiper != null) {
          this.gallerySwiper.slideTo(this.indexInParent(media));
          this.thumbsSwiper?.slideTo(this.indexInParent(media));
        }
      }

      matchResolution() {
        if (window.innerWidth < this.configuration.sliderEnabledBreakpoint) {
          return "mobile";
        } else {
          return "desktop";
        }
      }

      indexInParent(node) {
        if (!node) return -1;
        let children = node.parentNode.childNodes;
        let num = 0;
        for (let i = 0; i < children.length; i++) {
          if (children[i] == node) return num;
          if (children[i].nodeType == 1) num++;
        }
        return -1;
      }
    },
  );
}
